import { SupportedPaymentMethod } from '@mews/enums';
import { DEFAULT_CURRENCY_CODE } from '../../../../constants';
import { DEFAULT_LANGUAGE_CODE } from '../../../../defaultLanguage';
import { getCurrencyCode, getLanguageCode } from '../../../../modules/app/selectors';
import { getStandaloneDistributorUrl, isStandalone } from '../../../../modules/configuration/selectors';
import { generateGA4LinkerValue } from '../../../../modules/googleAnalytics/utils';
import { getSelectedHotelId } from '../../../../modules/resourceOrder/selectors';
import { trackEvent } from '../../../../services/logging/loggingHelpers';
import { ApplicationState, DistributorThunkAction, ThunkExtraArgument } from '../../../../types';
import { WithPaymentCardId, WithPaymentRequestId } from '../../../../types/selectors/selectorPropTypes';
import { getCardAuthorizationPageUrl, getPaymentRequestPageUrl, shouldDisplayPaymentCardForm } from '../viewSelectors';
import { insertConfirmationStepState } from './typedActions';

const getCommonPaymentGatewayData = (
    getState: () => ApplicationState,
    environmentService: ThunkExtraArgument['environmentService']
) => {
    const state = getState();
    const location = environmentService.get('location');
    const currentOrigin = location?.origin;
    const currentPathname = location?.pathname;
    const currentSearch = location?.search;
    const selectedHotelId = getSelectedHotelId(state);
    const languageCode = getLanguageCode(state) ?? DEFAULT_LANGUAGE_CODE;
    const currencyCode = getCurrencyCode(state) ?? DEFAULT_CURRENCY_CODE;

    const standalone = isStandalone(state);

    const isPaymentCardRequired = shouldDisplayPaymentCardForm(state, {});
    const paymentMethod = isPaymentCardRequired ? SupportedPaymentMethod.PaymentCard : '';

    return {
        currencyCode,
        languageCode,
        paymentMethod,
        search: currentSearch,
        enterpriseId: selectedHotelId,
        returnUrl: standalone ? `${currentOrigin}${currentPathname}` : getStandaloneDistributorUrl(state),
        propertyReturnUrl: standalone ? null : location?.href,
        ga4Linker: standalone ? null : generateGA4LinkerValue(Date.now()),
    };
};

interface CommonPaymentGatewayData {
    reservationGroupId: string;
    reservationIds: string[];
}

export const redirectToPaymentGatewayPaymentRequest =
    ({
        paymentRequestId,
        reservationGroupId,
        reservationIds,
    }: CommonPaymentGatewayData & WithPaymentRequestId): DistributorThunkAction =>
    (dispatch, getState, { environmentService }) => {
        const commonData = getCommonPaymentGatewayData(getState, environmentService);
        const paymentRequestPageUrl = getPaymentRequestPageUrl(getState(), {
            paymentRequestId,
            reservationGroupId,
            reservationIds,
            ...commonData,
        });

        dispatch(insertConfirmationStepState(paymentRequestPageUrl));
        dispatch(trackEvent('RedirectPaymentGateway', { enterpriseId: commonData.enterpriseId }));
        environmentService.set('location', paymentRequestPageUrl);
    };

export const redirectToPaymentGatewayCardAuthorization =
    ({
        paymentCardId,
        reservationGroupId,
        reservationIds,
    }: CommonPaymentGatewayData & WithPaymentCardId): DistributorThunkAction =>
    (dispatch, getState, { environmentService }) => {
        const commonData = getCommonPaymentGatewayData(getState, environmentService);
        const cardAuthorizationPageUrl = getCardAuthorizationPageUrl(getState(), {
            paymentCardId,
            reservationGroupId,
            reservationIds,
            ...commonData,
        });

        dispatch(insertConfirmationStepState(cardAuthorizationPageUrl));
        dispatch(trackEvent('Redirect3dsAuthorization', { enterpriseId: commonData.enterpriseId }));
        environmentService.set('location', cardAuthorizationPageUrl);
    };
